import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, inject, OnInit } from '@angular/core';
import {
  ReactiveFormsModule,
  UntypedFormControl,
  UntypedFormGroup,
  ValidationErrors,
  Validators,
} from '@angular/forms';
import { translate, TranslocoModule } from '@ngneat/transloco';
import { UntilDestroy } from '@ngneat/until-destroy';
import { isNil } from 'lodash';
import { MessageService, SharedModule } from 'primeng/api';
import { ChipsModule } from 'primeng/chips';
import { DialogService, DynamicDialogConfig, DynamicDialogRef } from 'primeng/dynamicdialog';
import { InputTextModule } from 'primeng/inputtext';
import { InputTextareaModule } from 'primeng/inputtextarea';
import { MessagesModule } from 'primeng/messages';
import { ToastModule } from 'primeng/toast';
import { firstValueFrom } from 'rxjs';

import { IVR, IVROption, IVROptionSchema, VoiceResponseCommandName } from '@pwp-common';

import { provideValidator } from '../../../../../common/form/provide-validator';
import { provideValueAccessor } from '../../../../../common/form/provide-value-accessor';
import { ArrayValidator } from '../../../../../common/validators/array-validator/array-validator';
import { IvrService } from '../../../../../services/call/ivr/ivr.service';
import { FormFieldHelpComponent } from '../../../../form/form-field-help/form-field-help.component';
import { FormGroupControlValueAccessor } from '../../../../generic/abstract-classes/form-group-control-value-accessor';
import { ConfirmWithInputComponent } from '../../../../generic/confirm-with-input/confirm-with-input.component';
import { SettingsModule } from '../../../../generic/settings/settings.module';
import { ProgressSpinnerModule } from '../../../progress-spinner/progress-spinner.module';
import { ServiceOperationEditorModule } from '../../../service-operation-editor/service-operation-editor.module';
import { dbToIVROptionEditorOutput } from '../editor-output/db-to-ivr-option-editor-output/db-to-ivr-option-editor-output';
import { formToIVROptionEditorOutput } from '../editor-output/form-to-ivr-option-editor-output/form-to-ivr-option-editor-output';
import { IVROptionEditorOutput } from '../editor-output/ivr-option-editor-output';
import { ivrOptionEditorOutputToDb } from '../editor-output/ivr-option-editor-output-to-db/ivr-option-editor-output-to-db';
import { IVROptionEditorFormType } from '../editor-output/ivr-option-form-type';

@UntilDestroy()
@Component({
  selector: 'app-ivr-option-editor',
  standalone: true,
  templateUrl: './ivr-option-editor.component.html',
  styleUrls: ['./ivr-option-editor.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    ChipsModule,
    CommonModule,
    FormFieldHelpComponent,
    InputTextModule,
    InputTextareaModule,
    MessagesModule,
    ProgressSpinnerModule,
    ReactiveFormsModule,
    ServiceOperationEditorModule,
    SettingsModule,
    SharedModule,
    ToastModule,
    TranslocoModule,
  ],
  providers: [
    MessageService,
    provideValueAccessor(() => IVROptionEditorComponent),
    provideValidator(() => IVROptionEditorComponent),
  ],
})
export class IVROptionEditorComponent
  extends FormGroupControlValueAccessor<IVROptionEditorFormType, IVROptionEditorOutput>
  implements OnInit
{
  ////////////////////////////////////////////////////////////////////////
  // Dependencies
  ////////////////////////////////////////////////////////////////////////

  private readonly dialogConfig = inject(DynamicDialogConfig);

  private readonly dialogRef = inject(DynamicDialogRef);

  private readonly dialogService = inject(DialogService);

  private readonly ivrService = inject(IvrService);

  private readonly messageService = inject(MessageService);

  ////////////////////////////////////////////////////////////////////////
  // State
  ////////////////////////////////////////////////////////////////////////

  public saving = false;

  public originalOption: IVROption | undefined;

  private ivr: IVR | undefined;

  private optionIndex: number | undefined;

  public readonly excludedCommands = [VoiceResponseCommandName.enforceServiceLimit];

  constructor(public changeDetectorRef: ChangeDetectorRef) {
    super(changeDetectorRef);
  }

  public override ngOnInit(): void {
    super.ngOnInit();

    const data = this.dialogConfig?.data;
    this.ivr = data?.ivr;
    this.optionIndex = data?.optionIndex;

    if (data?.ivrOption) {
      this.originalOption = data.ivrOption;
      this.writeValue(dbToIVROptionEditorOutput(this.originalOption));
    }
  }

  public defineForm(): void {
    this.form = new UntypedFormGroup({
      displayName: new UntypedFormControl('', [Validators.required, Validators.maxLength(500)]),
      description: new UntypedFormControl('', [Validators.required, Validators.maxLength(500)]),
      keyPresses: new UntypedFormControl([], [ArrayValidator.required(), ArrayValidator.regex(/^\d+$/)]),
      phrases: new UntypedFormControl([], [ArrayValidator.maxLength(IVROptionSchema.Constants.ivrHintItemMaxNumChars)]),
      commands: new UntypedFormControl({ commands: [] }, [Validators.required]),
    });
  }

  // eslint-disable-next-line class-methods-use-this
  public parseValueChange(value: IVROptionEditorFormType): IVROptionEditorOutput {
    return formToIVROptionEditorOutput(value);
  }

  /////////////////////////////////////////////////////////////////////////////////////////////
  // Validation Errors
  /////////////////////////////////////////////////////////////////////////////////////////////

  protected override makeValidationErrors(): ValidationErrors {
    return { 'ivr-option-editor': this.form.value };
  }

  ////////////////////////////////////////////////////////////////////////
  // Form Controls
  ////////////////////////////////////////////////////////////////////////

  public get displayName() {
    return this.form.get('displayName') as UntypedFormControl;
  }

  public get description() {
    return this.form.get('description') as UntypedFormControl;
  }

  public get keyPresses() {
    return this.form.get('keyPresses') as UntypedFormControl;
  }

  public get phrases() {
    return this.form.get('phrases') as UntypedFormControl;
  }

  public get commands() {
    return this.form.get('commands') as UntypedFormControl;
  }

  ////////////////////////////////////////////////////////////////////////
  // Upload
  ////////////////////////////////////////////////////////////////////////

  public doUpload = async (): Promise<void> => {
    if (!this.canSave()) {
      return;
    }

    const confirmed = await this.showSaveConfirmation();
    if (!confirmed) {
      this.dialogRef?.close(undefined);
      return;
    }

    await this.performSave();
  };

  // eslint-disable-next-line class-methods-use-this
  public refreshData = async (): Promise<void> => undefined;

  public resetForm = (): void => {
    this.dialogRef?.close(undefined);
  };

  public isValid = (): boolean => this.form.valid && !this.saving;

  ////////////////////////////////////////////////////////////////////////
  // Private
  ////////////////////////////////////////////////////////////////////////

  private canSave(): boolean {
    return this.isValid() && !isNil(this.originalOption) && !isNil(this.ivr) && !isNil(this.optionIndex);
  }

  private async showSaveConfirmation(): Promise<boolean> {
    const originalName = this.originalOption!.getDisplayName().trim();

    const dialogRef = this.dialogService.open(ConfirmWithInputComponent, {
      header: translate('ivr-option-editor.confirmSaveTitle'),
      width: '30%',
      data: {
        title: translate('ivr-option-editor.confirmSaveTitle'),
        body: translate('ivr-option-editor.confirmSaveBody'),
        requiredConfirmationString: originalName,
      },
    });

    return firstValueFrom(dialogRef.onClose);
  }

  private async performSave(): Promise<void> {
    this.saving = true;
    this.changeDetectorRef.detectChanges();

    try {
      const editorOutput = this.parseValueChange(this.form.value);
      const updatedOption = ivrOptionEditorOutputToDb({
        originalIvrOption: this.originalOption!,
        ivrOptionEditorOutput: editorOutput,
      });

      const updatedOptions = this.ivr!.getOptions().map((opt, idx) => (idx === this.optionIndex ? updatedOption : opt));
      this.ivr!.setOptions(updatedOptions);

      const updateSucceeded = await this.ivrService.uploadIfNotEditingStaleObject(this.ivr!);
      if (!updateSucceeded) {
        this.messageService.add({
          severity: 'error',
          summary: translate('ivr-option-editor.updateFailedTitle'),
          detail: translate('ivr-option-editor.updateFailedBody'),
          life: 5000,
        });
        return;
      }

      this.dialogRef?.close(true);
    } finally {
      this.saving = false;
      this.changeDetectorRef.detectChanges();
    }
  }
}
